import React, { useState, useCallback, useMemo, useEffect } from "react";
import Header from "../../components/Header";
import { CardWithoutSeeMore, CardWithStatus } from "../../components/Cards";
import SearchComponent from "../../components/Search";
import { debounce } from "lodash";
import { useNavigate, Link } from "react-router-dom";
import PaginationComponentOne from "../../components/Pagination/Pagination-O";
import { TimeframeLineChart } from "./InventoryCharts";
import { useSelector, useDispatch } from "react-redux";
import {
  getTotalStockValue,
  dashBoardStats,
  getInventorySummary,
  getMostRequestedProducts,
  getOutOfStockProducts,
  getActivityLogs,
  getExpiryAlertCount,
} from "../../redux/Inventory/inventoryActions";
import EmptyState from "../../components/EmptyState";
import IndexActivityLog from "./IndexActivityLog";

const ProductInventoryPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    totalStockValue,
    loading,
    totalActiveProducts,
    lowStockProducts,
    outOfStockProducts,
    inventorySummary,
    totalItems,
    mostRequestedProducts,
    outOfStockTotalCount,
    activityLogs,
    activityLogsLoading,
    expiryAlertCount,
  } = useSelector((state) => state.inventory);
  const [searchValue, setSearchValue] = useState("");
  const [tableFilter, setTableFilter] = useState("All");
  const [period, setPeriod] = useState("12 months");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handleSearch = useMemo(
    () =>
      debounce((val) => {
        setCurrentPage(1);
      }, 500),
    []
  );

  const handleChange = useCallback(
    (e) => {
      let val = e.target.value;
      setSearchValue(val);
      handleSearch(val);
    },
    [handleSearch]
  );

  const handlePageChange = (paginationData) => {
    // Carbon's Pagination component passes an object with page and pageSize
    if (typeof paginationData === "object") {
      if (paginationData.page !== undefined) {
        setCurrentPage(paginationData.page);
      }
      if (
        paginationData.pageSize !== undefined &&
        paginationData.pageSize !== pageSize
      ) {
        setPageSize(paginationData.pageSize);
        setCurrentPage(1); // Reset to first page when page size changes
      }
    } else {
      setCurrentPage(paginationData);
    }
  };

  const filteredInventorySummary = useMemo(() => {
    let filtered = inventorySummary;

    if (tableFilter === "Low Stock") {
      filtered = filtered.filter((product) => product.status === "low_stock");
    } else if (tableFilter === "Out of Stock") {
      filtered = filtered.filter(
        (product) => product.status === "out_of_stock"
      );
    }

    if (searchValue.trim() === "") return filtered;

    const searchTerm = searchValue.toLowerCase();

    return filtered.filter((product) => {
      const baseMatch =
        (product.product_name?.toLowerCase() || "").includes(searchTerm) ||
        (product.sku?.toLowerCase() || "").includes(searchTerm) ||
        (product.product_category?.toLowerCase() || "").includes(searchTerm);

      const variantMatch = product.variant_data
        ? (product.variant_data.type?.toLowerCase() || "").includes(
            searchTerm
          ) ||
          (product.variant_data.value?.toLowerCase() || "").includes(
            searchTerm
          ) ||
          (product.variant_data.unit?.toLowerCase() || "").includes(searchTerm)
        : false;

      return baseMatch || variantMatch;
    });
  }, [searchValue, inventorySummary, tableFilter]);

  useEffect(() => {
    // Fetch total stock value when the component mounts
    dispatch(getTotalStockValue());
    // Fetch dashboard stats
    dispatch(dashBoardStats());
    // Fetch inventory summary
    dispatch(getInventorySummary({ page: currentPage, pageSize }));

    dispatch(getMostRequestedProducts());
    // Fetch out of stock count for accurate display
    dispatch(getOutOfStockProducts({ page: 1 }));
    // Fetch top 3 activity logs
    dispatch(getActivityLogs({ page: 1, page_size: 3 }));

    dispatch(getExpiryAlertCount());
  }, [dispatch, currentPage, pageSize]);

  return (
    <>
      <Header text="Inventory" />
      <div className="grid grid-cols-3 gap-4 mt-6">
        <div className="col-span-2">
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg px-4 pt-4 pb-9 bg-[#FFFFFF] border border-[#E1E6E1] col-span-2">
              <p className="text-[#5F6D60] font-semibold text-sm">
                Total Stock Value
              </p>
              <h2 className="text-2xl font-semibold mt-5 text-[#344335]">
                ₦ {loading ? "0" : totalStockValue.toLocaleString()}
              </h2>
            </div>

            <CardWithoutSeeMore
              title="Active Products"
              subtitle={totalActiveProducts}
              href=""
              hasStatus
            />
          </div>

          <div className="grid grid-cols-3 gap-4 mt-4">
            <CardWithStatus
              title="Expiry Alert"
              subtitle={expiryAlertCount}
              href="/product-inventory/expiry-alert"
              hasStatus
              status="Urgent"
            />
            <CardWithStatus
              title="Low Stock Products"
              subtitle={lowStockProducts}
              href="/product-inventory/low-stock"
              hasStatus
              status="Urgent"
            />

            <CardWithStatus
              title="Out Of Stock Products"
              subtitle={outOfStockTotalCount || outOfStockProducts}
              href="/product-inventory/out-of-stock"
              hasStatus
              status="Urgent"
            />
          </div>

          <div className="border border-[#E1E6E1] rounded-lg mt-4 min-h-[360px] bg-white p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold text-sm text-[#5F6D60]">
                  Stock Movement
                </p>
              </div>
              <div>
                {/* Timeframe Dropdown */}
                <div className="mb-4 ">
                  <select
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    className="p-2 text-sm font-semibold text-[#5F6D60] focus:outline-none focus:ring-0"
                  >
                    <option value="12 months">12 Months</option>
                    <option value="3 months">3 Months</option>
                    <option value="7 days">7 Days</option>
                  </select>
                </div>
              </div>
            </div>
            <TimeframeLineChart period={period} />
          </div>
        </div>

        <div>
          {/* Most requested products */}
          <div className="bg-white rounded-lg border border-[#E1E6E1] ">
            <div className="border-b border-[#E1E6E1] px-4 py-4">
              <p className="text-sm text-[#3E473F] font-semibold">
                Most Requested Products
              </p>
            </div>
            <ul className="w-full max-h-[370px] overflow-y-auto">
              {loading ? (
                <div className="animate-pulse w-full px-4">
                  <div className="py-2 border-b border-[#E1E6E1]">
                    <div className="flex gap-1 mb-2">
                      <div className="h-6 bg-gray-200 rounded w-20"></div>
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                      <div className="h-6 bg-gray-200 rounded w-14"></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="h-4 bg-gray-200 rounded w-full mr-4"></div>
                      <div className="h-4 bg-gray-200 rounded w-8"></div>
                    </div>
                  </div>
                  <div className="py-2 border-b border-[#E1E6E1]">
                    <div className="flex gap-1 mb-2">
                      <div className="h-6 bg-gray-200 rounded w-24"></div>
                      <div className="h-6 bg-gray-200 rounded w-18"></div>
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="h-4 bg-gray-200 rounded w-full mr-4"></div>
                      <div className="h-4 bg-gray-200 rounded w-8"></div>
                    </div>
                  </div>
                  <div className="py-2 border-b border-[#E1E6E1]">
                    <div className="flex gap-1 mb-2">
                      <div className="h-6 bg-gray-200 rounded w-28"></div>
                      <div className="h-6 bg-gray-200 rounded w-14"></div>
                      <div className="h-6 bg-gray-200 rounded w-12"></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="h-4 bg-gray-200 rounded w-full mr-4"></div>
                      <div className="h-4 bg-gray-200 rounded w-8"></div>
                    </div>
                  </div>
                  <div className="py-2">
                    <div className="flex gap-1 mb-2">
                      <div className="h-6 bg-gray-200 rounded w-22"></div>
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                      <div className="h-6 bg-gray-200 rounded w-18"></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="h-4 bg-gray-200 rounded w-full mr-4"></div>
                      <div className="h-4 bg-gray-200 rounded w-8"></div>
                    </div>
                  </div>
                </div>
              ) : mostRequestedProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <EmptyState text="No requested products added yet!" />
                </div>
              ) : (
                mostRequestedProducts.map((product) => {
                  const maxLocationCount = Math.max(
                    ...mostRequestedProducts.map((p) => p.location_count)
                  );

                  // Handle products with variant data
                  if (product.variant_data) {
                    return (
                      <li
                        key={`${product.product_id}-${
                          product.variant_id || "no-variant"
                        }`}
                        className="w-full px-4 pb-2 border-b border-[#E1E6E1] last:border-b-0"
                      >
                        <div className="flex-col gap-1 items-center">
                          <div className="w-full truncate">
                            <div className="py-2 text-xs flex gap-1 font-normal text-[#5F6D60] whitespace-nowrap">
                              <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                                {product.product_name}
                              </p>
                              <span className="font-semibold text-lg">/</span>
                              <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                                {product.variant_data?.formulation ||
                                  product.variant_data?.type ||
                                  "N/A"}
                              </p>
                              <span className="font-semibold text-lg">/</span>
                              <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                                {product.variant_data?.value || "N/A"}
                                {product.variant_data?.unit || ""}
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <div className="w-full h-4">
                              <div
                                className="bg-[#2B8C34] h-4 rounded"
                                style={{
                                  width: `${Math.min(
                                    (product.location_count /
                                      maxLocationCount) *
                                      100,
                                    100
                                  )}%`,
                                }}
                              ></div>
                            </div>
                            <div className="min-w-[32px] text-center">
                              <p className="text-xs text-[#3E473F] font-semibold">
                                {product.location_count || 0}
                              </p>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  }

                  // Handle products without variants
                  return (
                    <li
                      key={product.product_id}
                      className="w-full px-4 pb-2 border-b border-[#E1E6E1] last:border-b-0"
                    >
                      <div className="flex-col gap-1 items-center">
                        <div className="w-full truncate">
                          <div className="py-2 text-xs flex gap-1 font-normal text-[#5F6D60] whitespace-nowrap">
                            <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                              {product.product_name}
                            </p>
                            <span className="font-semibold text-lg">/</span>
                            <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                              {product.product_category || "N/A"}
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="w-full h-4">
                            <div
                              className="bg-[#2B8C34] h-4 rounded"
                              style={{
                                width: `${
                                  (product.location_count / 1500) * 100
                                }%`,
                              }}
                            ></div>
                          </div>
                          <div className="min-w-[32px] text-center">
                            <p className="text-xs text-[#3E473F] font-semibold">
                              {product.location_count || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })
              )}
            </ul>
          </div>

          {/* Activity log */}
          <div className="bg-white border border-[#E1E6E1] rounded-lg h-fit mt-4">
            <div className="flex border-b border-[#E1E6E1] justify-between items-center p-4">
              <p className="text-sm text-[#2E332F] font-semibold">
                Activity Log
              </p>
              <Link
                to="/product-inventory/activity-log"
                className="text-sm text-[#2B8C34] font-semibold cursor-pointer hover:underline"
              >
                Show More
              </Link>
            </div>
            <div className="overflow-y-auto max-h-[360px]">
              {activityLogsLoading ? (
                <div className="animate-pulse w-full py-4">
                  <div className="h-12 bg-gray-200 rounded-lg w-full mb-4"></div>
                  <div className="h-12 bg-gray-200 rounded-lg w-full mb-4"></div>
                  <div className="h-12 bg-gray-200 rounded-lg w-full mb-4"></div>
                  <div className="h-12 bg-gray-200 rounded-lg w-full"></div>
                </div>
              ) : !activityLogs || activityLogs.length === 0 ? (
                <div className="py-8">
                  <EmptyState text="No recent activities" />
                </div>
              ) : (
                activityLogs.map((activity, index) => (
                  <IndexActivityLog key={index} activity={activity} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Inventory table */}
      <div className="mt-5 bg-white rounded-[8px] border border-[#E1E6E1] py-4 px-4 col-span-3">
        <div>
          <p className="text-[#3E473F] font-semibold text-lg">
            Inventory Table
          </p>
        </div>

        {/* search sectin */}
        <div className="mt-4">
          <SearchComponent
            placeholder="Search..."
            searchValue={searchValue}
            handleChange={handleChange}
          />
        </div>
        {/* All, Low Stock, Out of Stock Filters */}
        <div className="pb-3 w-full border-b border-[#E1E6E1]">
          <div className="text-sm font-semibold text-[#3E473F] border border-[#E1E6E1] mt-3  max-w-[576px] rounded-lg overflow-hidden flex">
            <button
              className={`px-3 py-2 text-center flex-1 ${
                tableFilter === "All" ? "bg-white rounded-lg" : "bg-[#F6F7F6]"
              }`}
              onClick={() => {
                setTableFilter("All");
              }}
            >
              All
            </button>
            <button
              className={`px-3 py-2 text-center flex-1 ${
                tableFilter === "Low Stock"
                  ? "bg-white rounded-lg"
                  : "bg-[#F6F7F6]"
              }`}
              onClick={() => {
                setTableFilter("Low Stock");
              }}
            >
              Low Stock
            </button>
            <button
              className={`px-3 py-2 text-center flex-1 ${
                tableFilter === "Out of Stock"
                  ? "bg-white rounded-lg"
                  : "bg-[#F6F7F6]"
              }`}
              onClick={() => {
                setTableFilter("Out of Stock");
              }}
            >
              Out of Stock
            </button>
          </div>
        </div>

        <div className="overflow-x-auto relative  py-4">
          {loading ? (
            <div className="animate-pulse w-full px-4 mb-4">
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-lg  w-full"></div>
            </div>
          ) : (
            <>
              {filteredInventorySummary.length === 0 ? (
                <EmptyState text="Your inventory will appear here once added!" />
              ) : (
                <table className="w-full text-left rounded-lg overflow-hidden border border-[#E1E6E1]">
                  <thead className="bg-[#F6F7F6] border-[#E1E6E1]">
                    <tr className="text-sm ">
                      <th className="px-4 py-4 font-semibold text-[#5F6D60] rounded-tl-lg rounded-bl-lg  ">
                        Product Name
                      </th>

                      <th
                        scope="col"
                        className="px-4 py-4 font-semibold text-[#5F6D60]"
                      >
                        SKU
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-4 font-semibold text-[#5F6D60]"
                      >
                        Category
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-4 font-semibold text-[#5F6D60]"
                      >
                        Total Stock
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-4 font-semibold text-[#5F6D60]"
                      >
                        Active Store
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-4 font-semibold text-[#5F6D60] rounded-tr-lg rounded-br-lg"
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white border-[#E1E6E1]">
                    {!loading &&
                      filteredInventorySummary.map((product) => {
                        // Handle products with variant data
                        if (product.variant_data && product.variant_id) {
                          return (
                            <tr
                              key={`${product.product_id}-${product.variant_id}`}
                              onClick={() =>
                                navigate(
                                  `/product-inventory/${product.product_id}?variant=${product.variant_id}`
                                )
                              }
                              className="text-sm cursor-pointer hover:bg-[#EDF7EE] border-b border-[#E1E6E1]"
                            >
                              <td className="px-4 py-4 flex font-normal text-[#5F6D60] whitespace-nowrap ">
                                <div className="flex items-center gap-1">
                                  <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                                    {product.product_name}
                                  </p>
                                  <span className="font-semibold text-lg">
                                    /
                                  </span>
                                  <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                                    {product.variant_data?.type}
                                  </p>
                                  <span className="font-semibold text-lg">
                                    /
                                  </span>
                                  <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                                    {product.variant_data?.value}
                                    {product.variant_data?.unit || ""}
                                  </p>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-[#5F6D60] font-semibold">
                                {product.sku}
                              </td>
                              <td className="px-4 py-4 text-[#5F6D60] font-semibold">
                                {product.product_category}
                              </td>
                              <td className="px-4 py-4 text-[#5F6D60]">
                                <p
                                  className={
                                    product.total_stock < 5
                                      ? "out-of-stock-badge"
                                      : product.total_stock < 20
                                      ? "low-stock-badge"
                                      : "in-stock-badge"
                                  }
                                >
                                  {product.total_stock}
                                </p>
                              </td>
                              <td className="px-4 py-4 text-[#344335]">
                                <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-medium">
                                  {product.location_count}
                                </p>
                              </td>
                              <td className="px- py-4 whitespace-nowrap">
                                <div className="relative">
                                  <div
                                    className={
                                      product.status === "low_stock"
                                        ? "low-stock-badge"
                                        : product.status === "out_of_stock"
                                        ? "out-of-stock-badge"
                                        : "in-stock-badge"
                                    }
                                  >
                                    <p>
                                      {product.status === "low_stock"
                                        ? "Low Stock"
                                        : product.status === "out_of_stock"
                                        ? "Out of Stock"
                                        : "In Stock"}
                                    </p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        // Handle products without variants
                        return (
                          <tr
                            key={product.product_id}
                            onClick={() =>
                              navigate(
                                `/product-inventory/${product.product_id}`
                              )
                            }
                            className="text-sm cursor-pointer hover:bg-[#EDF7EE] border-b border-[#E1E6E1]"
                          >
                            <td className="px-4 py-4 flex font-normal text-[#5F6D60] whitespace-nowrap ">
                              <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-semibold">
                                {product.product_name}
                              </p>
                            </td>
                            <td className="px-4 py-4 text-[#5F6D60] font-semibold">
                              {product?.sku || "N/A"}
                            </td>
                            <td className="px-4 py-4 text-[#5F6D60] font-semibold">
                              {product.product_category}
                            </td>
                            <td className="px-1 py-4 text-[#5F6D60]">
                              <p
                                className={
                                  product.total_stock < 5
                                    ? "out-of-stock-badge"
                                    : product.total_stock < 20
                                    ? "low-stock-badge"
                                    : "in-stock-badge"
                                }
                              >
                                {product.total_stock}
                              </p>
                            </td>
                            <td className="px-1 py-4 text-[#344335]">
                              <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-medium">
                                {product.location_count}
                              </p>
                            </td>
                            <td className="px-1 py-4 whitespace-nowrap">
                              <div className="relative">
                                <div
                                  className={
                                    product.status === "low_stock"
                                      ? "low-stock-badge"
                                      : product.status === "out_of_stock"
                                      ? "out-of-stock-badge"
                                      : "in-stock-badge"
                                  }
                                >
                                  <p>
                                    {product.status === "low_stock"
                                      ? "Low Stock"
                                      : product.status === "out_of_stock"
                                      ? "Out of Stock"
                                      : "In Stock"}
                                  </p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              )}
            </>
          )}
          {/* Figure out Pagination */}
          <div className="border-t border-[#E1E6E1]">
            <PaginationComponentOne
              totalItems={totalItems}
              page={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductInventoryPage;
